import { makeAutoObservable } from 'mobx';
import {t, api} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import Employee from 'main/stores/employees/Employee';
import {successAlert} from 'shared/tools';
import {DomainStore} from 'shared/store';
import _ from 'lodash';

class CompanyOnboardState {
  store = new DomainStore();
  EMAIL_REGEX = /^[^.@][^@]*@[^.@]+(\.[^.@]+)*\.[^@.]+$/;

  company;
  newEmployees = [];
  employees = [];
  employee = new Employee();
  previewInviteModalOpen = false;
  activateEmailModalOpen = false;
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.CURRENT_COMPANY,
      endpoints.COMPANY_EMAIL_TEMPLATES,
    ]);

    this.company = this.store._getSingle(types.COMPANY, {id: auth.company.id});
    this.employees = this.store.getAllOrdered(types.EMPLOYEE, Employee);
    this.emailPreview = this.store._getSingle(types.COMPANY_EMAIL_TEMPLATE, {emailType: 'employee_invitation'});
  }

  async saveEmployee(focusInputCallback) {
    const {model, errors} = await this.store.post(
      endpoints.COMPANY_ONBOARD.ADD_EMPLOYEE,
      types.EMPLOYEE_DETAILS,
      this.employee
    );
    this.errors = errors;

    if (model) {
      this.addNewEmployees([model]);
      this.employee = new Employee();
      focusInputCallback();
    }
  }

  async resendActivationEmail() {
    await api.post(endpoints.COMPANY_ONBOARD.RESEND_ACTIVATION_EMAIL);
    this.closePreviewInvite();
    successAlert(t('company_onboard.Email confirmation sent.'));
  }

  openPreviewInvite() {
    if (auth.user.activated) {
      this.previewInviteModalOpen = true;
    } else {
      this.activateEmailModalOpen = true;
    }
  }

  closePreviewInvite() {
    this.previewInviteModalOpen = false;
    this.activateEmailModalOpen = false;
  }

  addNewEmployees(employees) {
    const newEmployees = employees.map(e => new Employee(e));
    this.newEmployees = this.newEmployees.concat(newEmployees);
  }

  get currentEmployeeEmail() {
    return _.get(_.find(this.employees, { id: auth.employee.id }), 'invitationEmail');
  }

  get hasEmployees() {
    return this.employees.length > 1 || !!this.newEmployees.length;
  }

  get validEmployee() {
    const firstName = this.employee.firstName;
    const lastName = this.employee.lastName;

    if (!firstName || !lastName) return null;

    return firstName.replace(/\s/g, '') && lastName.replace(/\s/g, '') && this.validEmail;
  }

  get validEmail() {
    return this.EMAIL_REGEX.test(this.employee.invitationEmail);
  }

  get sortedEmployees() {
    return _.orderBy(this.employees, ['firstName'], ['desc']);
  }

  get sortedNewEmployees() {
    return _.orderBy(this.newEmployees, ['firstName'], ['desc']);
  }

  get stepOneComplete() {
    return this.hasEmployees;
  }

  get stepTwoComplete() {
    return auth.company.subscriptionEnabled;
  }
}

export default CompanyOnboardState;
